import { Locale } from "@/types/locale";
import type { VariantProps } from "class-variance-authority";
import React, { useCallback, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { ContainerWithHeaderSimple } from "@components/common/containers/container-with-header";
import { ContentEditBlock, ContentEditBlockProps } from "@components/common/content/content-edit-block";
import { FormSelectField } from "@components/common/forms/form-select-field";
import { FormTextField } from "@components/common/forms/form-text-field";
import { buttonVariants } from "@components/ui/button";
import { FieldDescription, FieldGroup, FieldLegend, FieldSeparator, FieldSet } from "@components/ui/field";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import { localizeFrom } from "@lib/i18n/utils";
import { buildBiographyReuseInput } from "@features/proposals/member-biography-tools";
import { useTranslateProposalFields } from "@features/proposals/hooks/proposal-translate-fields";
import { useGetActivityMemberBiographiesNoCacheQuery } from "@features/proposals/proposals-api";
import { ActivityMemberEntity, ActivityMemberInputView, ActivityMemberParticipationFormats, ActivityMemberRoles, ContributorBiographyInlineOutputView, getActivityMemberInputViewValidationSchema } from "@features/proposals/types/member";
import { MemberBiographyEditActions, MemberBiographyEditContent, MemberBiographyEditProvider } from "@features/proposals/ui/member/member-biography-edit-content";

/** Activity-specific adapter over the shared biography editor. */
export const MemberContentEditBlock = ({
  uploadPhoto,
  ...props
}: Omit<ContentEditBlockProps<ActivityMemberInputView, ActivityMemberEntity>, "title" | "getValidationSchema"> & VariantProps<typeof buttonVariants> & {
  uploadPhoto: (body: FormData) => Promise<void>;
}) => {
  const systemLocale = useLocale() as Locale;
  const t = useTranslations("features.proposals.ui.memberContentEditBlock");
  const tDomain = useTranslations("features.proposals.domain.activityMember.fields");
  const {
    activityMember
  } = useTranslateProposalFields();
  const {
    initData,
    patch
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("biography");
  const canListBiographies = Boolean(initData.rights.canListBiographies);
  const {
    data: biographies,
    isError: biographiesError,
    isFetching: biographiesLoading,
    refetch: refetchBiographies
  } = useGetActivityMemberBiographiesNoCacheQuery({
    memberId: initData.id
  }, {
    skip: !initData.id || !isOpen || !canListBiographies,
    refetchOnFocus: false,
    pollingInterval: 0
  });
  const reuseBiography = useCallback((biography: ContributorBiographyInlineOutputView) => patch(buildBiographyReuseInput(biography) as ActivityMemberInputView), [patch]);
  const saveActivityMember = useCallback(async (body: Partial<ActivityMemberInputView>) => {
    const updatedMember = await patch(body);
    setIsOpen(false);
    return updatedMember;
  }, [patch]);
  const biography_0 = initData.eventProfile?.biography;
  const firstName = localizeFrom(biography_0?.content?.data?.firstName, systemLocale);
  const lastName = localizeFrom(biography_0?.content?.data?.lastName, systemLocale);
  const fullName = firstName || lastName ? `${firstName} ${lastName}`.trim() : "-";
  const participationFormatOptions = ActivityMemberParticipationFormats.map(format => ({
    value: format,
    label: activityMember.translateParticipationFormat(format)
  }));
  const roleOptions = ActivityMemberRoles.map(role => ({
    value: role,
    label: activityMember.translateRole(role)
  }));
  return <MemberBiographyEditProvider biography={biography_0} biographies={biographies} biographiesLoading={Boolean(biographiesLoading)} biographiesError={Boolean(biographiesError)} patchRight={initData.rights.canPatch} canUploadPhoto={Boolean(initData.rights.canUploadPhoto)} isMe={initData.isMe} isOpen={isOpen} onRetryBiographies={canListBiographies ? () => refetchBiographies() : undefined} onReuseBiography={reuseBiography} onUploadPhoto={uploadPhoto}>
      <ContentEditBlock {...props} patch={saveActivityMember} title={fullName} getValidationSchema={getActivityMemberInputViewValidationSchema} notes={initData.isMe ? t("biography.alert.descriptionForMe") : t("biography.alert.description")} actions={activeTab === "biography" && <MemberBiographyEditActions />} actionsPosition="bottom" setIsOpenCallback={setIsOpen} keepOpenOnMutationError>
        {isOpen && <div className="-mx-3 mt-2 md:min-h-[80vh] md:min-w-2/3 lg:min-w-1/2 xl:min-w-1/3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid h-14 w-full grid-cols-2 justify-center rounded-none border-b p-2 md:justify-start">
                <TabsTrigger value="biography">{t("tabs.biography")}</TabsTrigger>
                <TabsTrigger value="participation">{t("tabs.participation")}</TabsTrigger>
              </TabsList>

              <TabsContent value="biography" className="flex flex-col gap-4 px-4 py-2">
                <MemberBiographyEditContent />
              </TabsContent>

              <TabsContent value="participation" className="flex flex-col gap-4 px-4 py-2">
                <ContainerWithHeaderSimple contentClassName="grid gap-6 spaced-y-6" headerLabel={t("biography.currentParticipation")}>
                  <FieldSet>
                    <FieldLegend>{t("biography.form.roleInfo.label")}</FieldLegend>
                    <FieldDescription>{t("biography.form.roleInfo.description")}</FieldDescription>
                    <FieldGroup className="grid grid-cols-2 items-baseline gap-4 px-4">
                      <FormSelectField name="role" rights={initData.rights.canPatch} label={tDomain("role.label")} description={tDomain("role.description")} options={roleOptions} placeholder={tDomain("role.placeholder")} className="w-2/3" />
                      <FormSelectField name="participationFormat" rights={initData.rights.canPatch} label={tDomain("participationFormat.label")} description={tDomain("participationFormat.description")} options={participationFormatOptions} placeholder={tDomain("participationFormat.placeholder")} className="w-2/3" />
                    </FieldGroup>
                    <FieldSeparator />
                  </FieldSet>

                  <FieldSet>
                    <FieldLegend>{tDomain("participationLocation.label")}</FieldLegend>
                    <FieldDescription>
                      {t("biography.form.locationInfo.description")}
                    </FieldDescription>
                    <FieldGroup className="grid grid-cols-2 items-baseline gap-4 px-4">
                      <FormTextField name="participationLocation.timezone" rights={initData.rights.canPatch} label={tDomain("participationLocation.timezone.label")} description={tDomain("participationLocation.timezone.description")} placeholder={tDomain("participationLocation.timezone.placeholder")} className="w-2/3" />
                      <FormTextField name="participationLocation.country" rights={initData.rights.canPatch} label={tDomain("participationLocation.country.label")} description={tDomain("participationLocation.country.description")} placeholder={tDomain("participationLocation.country.placeholder")} className="w-2/3" />
                      <FormTextField name="participationLocation.city" label={tDomain("participationLocation.city.label")} description={tDomain("participationLocation.city.description")} placeholder={tDomain("participationLocation.city.placeholder")} className="col-start-2 w-2/3" rights={initData.rights.canPatch} />
                    </FieldGroup>
                  </FieldSet>
                </ContainerWithHeaderSimple>
              </TabsContent>
            </Tabs>
          </div>}
      </ContentEditBlock>
    </MemberBiographyEditProvider>;
};